import { Component, DestroyRef, inject } from '@angular/core';
import {
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { PartyFormGroup } from '../../models/user-details-form';
import { PartyService } from '../../services/party.service';
import { catchError, NEVER, tap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse } from '@angular/common/http';

const EXCLUDED_PARTY_NUMBERS = ['111111111', '222222222'];

function partyNumberValidator(control: AbstractControl<string>): ValidationErrors | null {
  const value = control.value;
  if (!value) {
    return null;
  }
  if (!/^\d{9}$/.test(value)) {
    return { partyNumberFormat: true };
  }
  if (!/^[12]/.test(value)) {
    return { partyNumberPrefix: true };
  }
  if (EXCLUDED_PARTY_NUMBERS.includes(value)) {
    return { partyNumberExcluded: true };
  }
  return null;
}

@Component({
  selector: 'app-create-party',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatFormField,
    MatLabel,
    ReactiveFormsModule,
    MatInput,
    MatError
  ],
  templateUrl: './create-party.component.html',
  styleUrl: './create-party.component.scss'
})
export class CreatePartyComponent {
  saving = false;
  form: PartyFormGroup = new FormGroup({
    number: new FormControl('', {
      validators: [Validators.required, partyNumberValidator],
      nonNullable: true
    }),
    name: new FormControl('', { validators: Validators.required, nonNullable: true })
  });

  private dialogRef = inject(MatDialogRef<CreatePartyComponent>);
  private partyService = inject(PartyService);
  private destroyRef = inject(DestroyRef);
  private matSnackBar = inject(MatSnackBar);

  create() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    this.partyService
      .create(this.form.getRawValue())
      .pipe(
        tap(() => {
          this.matSnackBar.open('Party created successfully', 'x', { panelClass: 'snackbar-green' });
          this.dialogRef.close();
          this.saving = false;
        }),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 409) {
            this.matSnackBar.open('Party with this ID already exists', 'x');
          } else if (error.status === 400) {
            this.matSnackBar.open('Invalid party number. Please check and try again.', 'x');
          } else {
            console.error(error);
            this.matSnackBar.open('Could not create the party. Please try again.', 'x');
          }
          this.saving = false;
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
